import { useState } from 'react'
import { Plus } from 'lucide-react'

interface Props {
  onAdd: (title: string) => Promise<void>
  onMore: (title: string) => void
  autoFocus?: boolean
  /** The habit list says "a name is enough"; the default is the task list's line. */
  placeholder?: string
}

/**
 * Capture at the top of the list (redesign 2026-09-05). A title is enough —
 * Enter saves it and keeps the box focused for the next one; "More options"
 * carries the typed title into the full form. Never navigates away on its
 * own: the new row appears in the list below.
 */
export function TaskQuickAdd({ onAdd, onMore, autoFocus = false, placeholder = 'Add a task — a title is enough' }: Props) {
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async () => {
    const t = title.trim()
    if (!t || busy) return
    setBusy(true); setErr(null)
    try {
      await onAdd(t)
      setTitle('')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not add that')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); void submit() }}
      className="rounded-lg border border-dashed border-strong bg-surface px-2 py-1 transition-colors focus-within:border-brand sm:px-3"
      data-testid="task-quick-add"
    >
      <div className="flex items-center gap-2">
        <Plus className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setTitle(''); (e.target as HTMLInputElement).blur() } }}
          placeholder={placeholder}
          aria-label={placeholder.split(/\s[—-]\s/)[0]}
          autoFocus={autoFocus}
          disabled={busy}
          className="min-h-touch flex-1 bg-transparent text-sm text-primary placeholder:text-muted focus:outline-none lg:min-h-0 lg:py-2"
          data-testid="task-quick-add-input"
        />
        {title.trim() && (
          <button type="submit" disabled={busy} className="shrink-0 text-sm font-medium text-brand hover:underline disabled:opacity-60" data-testid="task-quick-add-save">
            {busy ? 'Adding…' : 'Add'}
          </button>
        )}
        <button type="button" onClick={() => onMore(title.trim())} className="shrink-0 text-app-micro text-muted hover:text-brand" data-testid="task-quick-add-more">
          More options
        </button>
      </div>
      {err && <p className="pb-1 pl-6 text-xs text-bad" role="alert">{err}</p>}
    </form>
  )
}

export default TaskQuickAdd
